<!-- eslint-disable max-len -->
<template>
	<div v-if="activeProduct.pricing.price" class="space-y-sm">
		<!-- Regular price layout. If there's a club price available we use a different layout, so check for that first. -->
		<div
			v-if="!(clubPrice && clubSaving)"
			class="flex flex-col items-start gap-y-xs xl:flex-row"
			:class="{
				// If countdown timer is shown, there's less space so in that case we don't try to align the promotion info component with the strikethrough price
				'xl:items-end': !isCountdownShown,
			}"
		>
			<!-- Using a TW class conditionally didn't work here! -->
			<div
				class="flex flex-row items-end"
				:style="{
					filter: activeProductPending ? 'blur(9px)' : 'none',
				}"
			>
				<!-- Effective current price first (exact or an estimate if user hasn't selected a specific variant yet) -->
				<v-h2 v-if="productSelectionIncomplete" class="text-red-600 lowercase">
					{{ t("product-part-payment-prefix") }}
					{{ formatCurrency(activeProduct.pricing.price.amount) }}
				</v-h2>

				<v-h2 v-else class="text-red-600">
					{{ formatCurrency(activeProduct.pricing.price.amount) }}
				</v-h2>

				<!-- If the effective price is discounted, also show the non-discounted price with a strikethrough and smaller font -->
				<!-- Note that we explicitly avoid showing the original price if this is an omnibus campaign, omnibus promoted products should always be going at their lowest price already. As for text campaigns, we want to explicitly treat these as always text-only, there are no promotions involved. HOWEVER, we don't want to do this in Estonia since they're using text-style campaigns for promoted products too. -->
				<p
					v-if="
						promotionInfo.style?.type !== PromotionStyleType.Omnibus &&
						(promotionInfo.style?.type !== PromotionStyleType.Text ||
							locale === 'et') &&
						activeProduct.pricing.originalPrice &&
						activeProduct.pricing.price.amount !==
							activeProduct.pricing.originalPrice.amount
					"
					class="mb-xs ml-xs text-sm tracking-xl text-[#5A5454] line-through"
				>
					{{ formatCurrency(activeProduct.pricing.originalPrice.amount) }}
				</p>
			</div>

			<!-- Info about the promotion -->
			<div class="block w-full lg:w-2/3 xl:ml-xl xl:w-1/2">
				<v-page-product-promotion-info
					class="mt-xs"
					:class="{
						// If countdown timer is shown, there's less space so in that case we don't try to align the promotion info component with the strikethrough price
						'xl:mb-xs': !isCountdownShown,
					}"
					:pending="!isClientside || promotionInfoPending"
					:promotion-info="promotionInfo"
				/>
			</div>
		</div>

		<!-- Club price layout -->
		<div v-else class="flex flex-col">
			<!-- Original product price and for logged-in users also the club price and possible promotion info. The order of the elements varies neatly between screen sizes with flex. -->
			<div class="flex flex-wrap items-end gap-x-4 gap-y-xs">
				<!--
                    Badge with minimum width of 13 rem.
                    It's always a bit of an compromise when trying to define somewhat static width
                    for element which has variable content length.

                    Using the 13rem as width seemed to be best compromise on keeping the club layout for
                    authenticated/non-authenticatd users without the layout having too much spacing overall,
                    due to the somewhat static width.
                  -->
				<v-page-product-club-badge
					class="w-fit min-w-52"
					:club-price="clubPrice"
					:club-saving="clubSaving"
				/>

				<!--
					Effective price.

					Products might have: discounted club price (e.g. -10%) and regular catalog promotion (e.g. -10%)

					This means that club users will get -10% (from the catalog promotion) and -10% from the club promotion
					If we have both of those promotions active at the same time, non-logged in users should always see the effective price (not the original price)
				-->

				<!-- Shown as strikethrough if the user is logged in and sees their effective club price above, otherwise shown as the main price. -->
				<p
					v-if="activeProduct.pricing.price"
					class="mb-1 xl:order-2 xl:basis-full"
					:class="{
						'mt-1 text-2xl text-[#5A5454] line-through': hasProfile,
						'font-prenton-cond text-4xl font-bold tracking-xl': !hasProfile,
					}"
				>
					{{ formatCurrency(activeProduct.pricing.price.amount) }}
				</p>

				<!-- Club promotion info. This is shown if the product is both in a cart-level club promotion and also has a product-level club promotion with a campaign style that has product page settings selected (such as showing a countdown timer). -->
				<div class="w-full lg:w-2/3 xl:order-1 xl:ml-lg xl:w-1/2">
					<v-page-product-promotion-info
						class="mt-xs"
						:pending="!isClientside || promotionInfoPending"
						:promotion-info="promotionInfo"
					/>
				</div>
			</div>
		</div>

		<!-- Possible liter price separately -->
		<div v-if="calculateLiterPrice !== undefined" class="flex flex-col">
			<p class="text-base tracking-xl text-[#5A5454]">
				{{ formatCurrency(calculateLiterPrice, {maximumFractionDigits: 2}) }}/L
			</p>
		</div>

		<!-- If not super cheap product, show how much it would cost in parts -->
		<div class="flex flex-col items-start">
			<v-page-product-installments-button
				v-if="Number(activeProduct.pricing.price.amount) > 50"
				:price="activeProduct.pricing.price.amount"
			/>

			<div
				v-if="!hidePriceHistory && omnibusContent"
				class="pt-xs text-sm tracking-xl"
			>
				<div class="flex flex-row items-center">
					<v-icon icon="tag" class="mr-xs text-lg" />
					{{ omnibusContent }}
				</div>

				<div
					v-if="
						promotionInfo.style?.type === PromotionStyleType.Omnibus &&
						activeProduct.pricing.originalPrice &&
						activeProduct.pricing.price.amount !==
							activeProduct.pricing.originalPrice.amount
					"
					class="mt-1 ml-[26px]"
				>
					{{ t("regular-price") }}
					{{ formatCurrency(activeProduct.pricing.originalPrice.amount) }}
					(-{{ productDiscountPercentage }}%)
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type {PromotionInfo} from "@pkg/shared/types";
import {
	type SimpleProductDataFragment,
	type ViewProductDetailsQuery,
	PromotionStyleType,
} from "@pkg/shared/graphql";

import {useClubPromotion} from "@pkg/shared/composables/use-club-promotion";
import {useCurrency} from "@pkg/shared/composables/use-currency";
import {useCustomerLogin} from "@pkg/shared/composables/use-customer-login";

type DetailProduct = Extract<
	ViewProductDetailsQuery["product"],
	{__typename: "SimpleProduct"}
>;

const properties = defineProps<{
	productSelectionIncomplete: boolean;
	activeProduct: SimpleProductDataFragment;
	activeProductPending: boolean;
	dimensions: DetailProduct["dimensions"] | undefined;
	promotionInfo: PromotionInfo;
	promotionInfoPending: boolean;
	hidePriceHistory?: boolean;
	lowestPrice?: string;
	omnibusContent: string;
}>();

const calculateLiterPrice = computed(() =>
	properties.dimensions?.volume &&
	properties.activeProduct.pricing?.price?.amount
		? (Number(properties.activeProduct.pricing?.price?.amount) /
				properties.dimensions.volume) *
			100
		: undefined,
);

const {t, locale} = useI18n({
	useScope: "local",
});

const {hasProfile} = useCustomerLogin();

const {format: formatCurrency} = useCurrency();

const isClientside = ref(false);

const {clubPrice, discount: clubSaving} = useClubPromotion(
	computed(() => properties.activeProduct),
	"product",
);

const priceRegex = /#price/i;

const omnibusContent = computed<string>(() =>
	properties.omnibusContent.replace(priceRegex, () =>
		formatCurrency(Number(properties.lowestPrice)),
	),
);

const isWithin48Hours = computed(() => {
	const now = new Date();
	const timeDifference =
		(properties.promotionInfo.activeTo ?? new Date()).getTime() - now.getTime();
	const hoursDifference = timeDifference / (1000 * 60 * 60);

	return hoursDifference > 0 && hoursDifference <= 48;
});

const timerHasLessThan48hoursRemaining = computed(
	() =>
		properties.promotionInfo.showEndingTimer &&
		properties.promotionInfo.isTimedVisibility &&
		isWithin48Hours.value,
);

// This should mirror the promotion-info component's inner logic - kinda ugly but works
const isCountdownShown = computed(
	() =>
		isClientside.value &&
		properties.promotionInfo.showEndingTimer &&
		(!properties.promotionInfo.isTimedVisibility ||
			timerHasLessThan48hoursRemaining.value),
);

const productDiscountPercentage = computed(() => {
	const {price, originalPrice} = properties.activeProduct.pricing;
	if (!(price && originalPrice) || price.amount === originalPrice.amount) {
		return 0;
	}
	const priceNum = Number(price.amount);
	const originalPriceNum = Number(originalPrice.amount);
	if (originalPriceNum === 0) {
		return 0;
	}
	return Math.round((1 - priceNum / originalPriceNum) * 100);
});

onBeforeMount(() => {
	isClientside.value = true;
});
</script>

<i18n lang="json">
{
	"et": {
		"product-part-payment": "Maksa osade kaupa ",
		"product-part-payment-prefix": "al.",
		"product-part-payment-suffix": "/kuu",
		"regular-price": "Tavahind",
		"lowest-price-in-the-last-30-days": "30 päeva madalaim hind"
	},
	"fi": {
		"product-part-payment": "Maksa erissä ",
		"product-part-payment-prefix": "alk.",
		"product-part-payment-suffix": "/kk",
		"regular-price": "Normaalihinta",
		"lowest-price-in-the-last-30-days": "Alin hinta 30 pv"
	}
}
</i18n>
